import { Box, HStack, VStack } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import React, { useCallback, useRef, useState } from 'react';

import {
  ReportGeneratorCategory,
  ReportGeneratorSubCategory,
} from '../../../../../store/reportGenerator/categories';
import { ReportCategoryCards, ReportCategoryCardsHandle } from '../../ReportCategoryCards';
import { CarouselPageIndicators } from '../../ReportCategoryCards/CarouselPageIndicators';
import { ReportCategoryTags } from '../../ReportCategoryTags';

interface ReportCatalogSectionProps {
  categories: ReportGeneratorCategory[];
  catalogCategoryFilterId: string;
  visibleSubCategories: ReportGeneratorSubCategory[];
  showCategoryTabs: boolean;
  onSelectCategoryFilter: (categoryFilterId: string) => void;
  onGenerateReport: (subCategory: ReportGeneratorSubCategory) => void;
}

export const ReportCatalogSection: React.FC<ReportCatalogSectionProps> = ({
  categories,
  catalogCategoryFilterId,
  visibleSubCategories,
  showCategoryTabs,
  onSelectCategoryFilter,
  onGenerateReport,
}) => {
  const cardsRef = useRef<ReportCategoryCardsHandle>(null);
  const [carouselActivePage, setCarouselActivePage] = useState(0);
  const [carouselPageCount, setCarouselPageCount] = useState(1);
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const handleCarouselPageChange = useCallback((page: number, pageCount: number) => {
    setCarouselActivePage((current) => (current === page ? current : page));
    setCarouselPageCount((current) => (current === pageCount ? current : pageCount));
  }, []);

  const categoryTags = showCategoryTabs ? (
    <ReportCategoryTags
      categories={categories}
      selectedCategoryFilterId={catalogCategoryFilterId}
      onSelectCategoryFilter={onSelectCategoryFilter}
    />
  ) : null;

  const pageIndicators = (
    <CarouselPageIndicators
      pageCount={carouselPageCount}
      activePage={carouselActivePage}
      onPagePress={(page) => {
        setCarouselActivePage(page);
        cardsRef.current?.scrollToPage(page);
      }}
    />
  );

  return (
    <VStack width="full" space="stack.xxxs">
      {isMobile ? (
        <>
          {pageIndicators}
          {categoryTags}
        </>
      ) : (
        <HStack width="full" alignItems="center" space="inline.nano">
          {categoryTags ? (
            <Box flex={1} minW={0}>
              {categoryTags}
            </Box>
          ) : null}
          {pageIndicators}
        </HStack>
      )}

      <ReportCategoryCards
        ref={cardsRef}
        subCategories={visibleSubCategories}
        onGenerateReport={onGenerateReport}
        onActivePageChange={handleCarouselPageChange}
      />
    </VStack>
  );
};
